import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { ArrowLeft, Upload, Award, Download, FileSpreadsheet, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const TYPES = ["5_year_camper", "5_year_staff", "10_year_camper", "10_year_staff"];
const LABELS = {
  "5_year_camper": "5 Year Camper", "5_year_staff": "5 Year Staff",
  "10_year_camper": "10 Year Camper", "10_year_staff": "10 Year Staff",
};

export default function MilestoneCertificatesView({ token, onBack }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [templates, setTemplates] = useState({});
  const [uploadingType, setUploadingType] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [batch, setBatch] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const loadTemplates = () => {
    axios.get(`${API}/admin/certificates/templates`, { headers })
      .then(res => setTemplates(res.data?.templates || {}))
      .catch(() => toast.error("Failed to load templates"));
  };
  useEffect(loadTemplates, []);

  const uploadTemplate = async (type, file) => {
    if (!file) return;
    setUploadingType(type);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/admin/certificates/template/${type}`, fd, { headers });
      toast.success(`${LABELS[type]} template saved`);
      loadTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Template upload failed");
    } finally {
      setUploadingType(null);
    }
  };

  const generate = async (file) => {
    if (!file) return;
    setGenerating(true);
    setBatch(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/admin/certificates/generate`, fd, { headers, timeout: 300000 });
      setBatch(res.data);
      if ((res.data.missing_tabs || []).length) {
        toast.warning(`No tab found for: ${res.data.missing_tabs.join(", ")}`);
      }
      toast.success("Certificates generated — review the previews below");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const downloadFile = async (url, filename, key) => {
    setDownloading(key);
    try {
      const res = await axios.get(url, { headers, responseType: "blob", timeout: 300000 });
      const blobUrl = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl; a.download = filename; a.click();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error("Download failed");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="milestone-certificates-view">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="certificates-back">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <h2 className="font-bold text-lg flex items-center gap-2 text-gray-900">
          <Award className="h-5 w-5 text-amber-600" /> Milestone Certificates
        </h2>
      </div>

      <Card><CardContent className="p-4">
        <p className="text-sm font-bold text-gray-800 mb-1">1 · Certificate Templates</p>
        <p className="text-xs text-gray-500 mb-3">Upload/replace each year's template image (11×8.5 landscape). The name is overlaid — nothing else is changed.</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {TYPES.map(t => {
            const tpl = templates[t];
            return (
              <div key={t} className="border rounded-lg p-2 text-center space-y-2" data-testid={`certificates-template-${t}`}>
                <p className="text-xs font-bold text-gray-700">{LABELS[t]}</p>
                {tpl?.thumb_b64
                  ? <img src={`data:image/jpeg;base64,${tpl.thumb_b64}`} alt={LABELS[t]} className="w-full rounded border" />
                  : <div className="h-24 flex items-center justify-center text-[10px] text-gray-400 bg-gray-50 rounded border border-dashed">No template yet</div>}
                <label className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 cursor-pointer hover:underline">
                  <Upload className="h-3 w-3" /> {uploadingType === t ? "Uploading…" : tpl ? "Replace" : "Upload"}
                  <input type="file" accept="image/*" className="hidden" data-testid={`certificates-template-input-${t}`}
                    onChange={e => uploadTemplate(t, e.target.files?.[0])} disabled={uploadingType === t} />
                </label>
                {tpl?.uploaded_at && <p className="text-[9px] text-gray-400">{new Date(tpl.uploaded_at).toLocaleDateString()}</p>}
              </div>
            );
          })}
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-4">
        <p className="text-sm font-bold text-gray-800 mb-1">2 · Roster Spreadsheet (.xlsx)</p>
        <p className="text-xs text-gray-500 mb-3">Four tabs like "2026 5 year camper" … each with First / Last columns. Generates one multi-page PDF per type.</p>
        <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold cursor-pointer">
          <FileSpreadsheet className="h-4 w-4" />
          {generating ? "Generating…" : "Upload Roster & Generate"}
          <input type="file" accept=".xlsx" className="hidden" data-testid="certificates-roster-input"
            onChange={e => { generate(e.target.files?.[0]); e.target.value = ""; }} disabled={generating} />
        </label>
        {generating && <span className="ml-3 text-xs text-gray-500 inline-flex items-center gap-1"><RefreshCw className="h-3 w-3 animate-spin" /> Building PDFs…</span>}
      </CardContent></Card>

      {batch && (
        <Card className="border-emerald-200"><CardContent className="p-4 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm font-bold text-emerald-800">3 · Preview & Download ({batch.year})</p>
            <Button size="sm" onClick={() => downloadFile(`${API}/admin/certificates/download-all/${batch.batch_id}`, "Milestone_Certificates.zip", "all")}
              disabled={downloading === "all"} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="certificates-download-all">
              <Download className="h-4 w-4 mr-1" /> Download All (zip)
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {(batch.results || []).map(r => (
              <div key={r.type} className="border rounded-lg p-3 space-y-2" data-testid={`certificates-result-${r.type}`}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-bold text-gray-800">{r.label}</p>
                  <Badge variant="outline" className="text-[10px]">{r.count} page{r.count === 1 ? "" : "s"}</Badge>
                </div>
                {r.preview_b64
                  ? <img src={`data:image/png;base64,${r.preview_b64}`} alt={`${r.label} preview`} className="w-full rounded border shadow-sm" data-testid={`certificates-preview-${r.type}`} />
                  : <p className="text-xs text-gray-400 italic">{r.note || "No preview"}</p>}
                {r.first_name_on_page1 && <p className="text-[10px] text-gray-500">Page 1: {r.first_name_on_page1}</p>}
                {r.filename && (
                  <Button size="sm" variant="outline" className="w-full"
                    onClick={() => downloadFile(`${API}/admin/certificates/download/${batch.batch_id}/${r.type}`, r.filename, r.type)}
                    disabled={downloading === r.type} data-testid={`certificates-download-${r.type}`}>
                    <Download className="h-3.5 w-3.5 mr-1" /> {downloading === r.type ? "Downloading…" : r.filename}
                  </Button>
                )}
              </div>
            ))}
          </div>
        </CardContent></Card>
      )}
    </div>
  );
}
